import { useStoredState } from 'custom_hooks/useStorage';
import {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { DefaultTheme, StyleSheetManager, ThemeProvider } from 'styled-components';
import { GlobalThemeStyles } from 'styles/themes/GlobalThemeStyles';
import { ThemeType } from 'styles/themes/KCFTheme';

type SetColorModeOptions = {
    /**
     * @param options.transient whether the selection should be transient (i.e.
     * it should not be persisted)
     */
    transient?: boolean;
};

export type IThemeContext = DefaultTheme & {
    /**
     * @param colorMode the theme to switch to
     */
    setColorMode: (colorMode: ThemeType, options?: SetColorModeOptions) => void;

    /**
     * sets the theme back to the stored value
     */
    restoreColorMode: () => void;
};

const ThemeToggleContext = createContext<IThemeContext>({
    setColorMode: () => {},
    restoreColorMode: () => {},
    colorMode: ThemeType.light,
});

export const useTheme = () => useContext(ThemeToggleContext);

export function KCFStylesProvider({
    children,
    theme,
}: {
    children: ReactNode;
    theme?: ThemeType | undefined;
}): JSX.Element {
    const [storedColorMode, setStoredColorMode] = useStoredState(
        theme ?? ThemeType.light,
        ThemeType.light,
        {
            store: 'local',
            version: 1,
        }
    );
    const [memoryColorMode, setMemoryColorMode] = useState(storedColorMode);
    const modesRef = useRef({ memoryColorMode, storedColorMode });
    modesRef.current.memoryColorMode = memoryColorMode;
    modesRef.current.storedColorMode = storedColorMode;

    const settersCtx = useMemo(
        () => ({
            setColorMode: (cm: ThemeType, options?: SetColorModeOptions) => {
                setMemoryColorMode(cm);
                if (options?.transient !== true) {
                    setStoredColorMode(cm);
                }
            },
            restoreColorMode: () => {
                if (
                    modesRef.current.memoryColorMode !== modesRef.current.storedColorMode
                ) {
                    setMemoryColorMode(modesRef.current.storedColorMode);
                }
            },
        }),
        [setStoredColorMode]
    );

    const ctx = useMemo(
        () => ({
            ...settersCtx,
            colorMode: memoryColorMode,
        }),
        [memoryColorMode, settersCtx]
    );

    useEffect(() => {
        if (theme) {
            setStoredColorMode(theme);
            setMemoryColorMode(theme);
        }
        localStorage.setItem('sdLastTheme', storedColorMode);
    }, [setStoredColorMode, storedColorMode, theme]);

    return (
        <ThemeToggleContext.Provider value={ctx}>
            <StyleSheetManager>
                <ThemeProvider
                    theme={{
                        colorMode: memoryColorMode,
                    }}
                >
                    <GlobalThemeStyles />
                    {children}
                </ThemeProvider>
            </StyleSheetManager>
        </ThemeToggleContext.Provider>
    );
}

export { ThemeProvider };
